import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { matchPath, useLocation } from 'react-router';
import {
  DecisionTreeRunWithParentFieldsFragment,
  ListDecisionTreesQuery,
  useGetDecisionTreeRunQuery, useStartDecisionTreeRunMutation, useStopDecisionTreeRunMutation, useUpdateDecisionTreeRunHistoryMutation,
  useWriteDecisionTreeRunCommentMutation,
} from '../../../../generated/core-api/graphql-react-query';
import useHistory from '../../../Hooks/useHistory/useHistory';
import useSessionContext from '../../../LiveContext/SessionContextProvider/useSessionContext';
import useGlobalStoreContext from '../../GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import { DecisionTreeRunContext } from './useDecisionTreeRunContext';
import useUpdateSessionDecisionTreeRun from './useUpdateSessionDecisionTreeRun';

function useRunUpdater() {
  const queryClient = useQueryClient();
  const { projectId: [projectId] } = useGlobalStoreContext();
  const updateSessionRun = useUpdateSessionDecisionTreeRun();

  const updateTreeRun = React.useCallback((value: DecisionTreeRunWithParentFieldsFragment) => {
    queryClient.setQueryData(['getDecisionTreeRun', { input: { id: value.id } }], { getDecisionTreeRun: value });

    queryClient.setQueryData(['listDecisionTrees', { input: { id: projectId ?? '' } }], (prev: ListDecisionTreesQuery | null) => {
      if (!prev?.getProject) {
        return prev;
      }
      const trees = prev.getProject.decisionTrees ?? [];
      const updatedTrees = trees.map((tree) => {
        if (tree.id === value.decisionTreeId) {
          return {
            ...tree,
            latestRun: value,
          };
        }
        return tree;
      });
      return {
        getProject: {
          ...prev.getProject,
          decisionTrees: updatedTrees,
        },
      };
    });
  }, [projectId]);

  const update = React.useCallback((value: DecisionTreeRunWithParentFieldsFragment) => {
    updateTreeRun(value);
    updateSessionRun(value);

    if (value.parentId && value.parent) {
      updateTreeRun(value.parent);
    }
  }, [updateSessionRun, updateTreeRun]);

  return update;
}

export default function DecisionTreeRunContextProvider({ children }: { children: React.ReactNode }) {
  const { projectId: [projectId] } = useGlobalStoreContext();
  const location = useLocation();
  const { navigate } = useHistory();
  const [previewRunId, setPreviewRunId] = React.useState<string | null>(null);
  const routeRunId = React.useMemo(
    () => matchPath('/decision-trees/run/:runId', location.pathname)?.params.runId ?? null,
    [location.pathname],
  );
  const isPreview = React.useMemo(() => new URLSearchParams(location.search).has('preview-dt'), [location.search]);
  const runId = routeRunId ?? previewRunId;

  React.useEffect(() => {
    if (!isPreview) {
      setPreviewRunId(null);
    }
  }, [isPreview]);

  const decisionTreeRun = useGetDecisionTreeRunQuery({ input: { id: runId ?? '' } }, { enabled: !!runId, staleTime: Infinity });
  const isDecisionTreeRoute = React.useMemo(() => !!matchPath('/decision-trees/*', location.pathname), [location.pathname]);

  const { sessionId } = useSessionContext();

  const queryClient = useQueryClient();
  const updateRun = useRunUpdater();

  const updateDecisionTreeRunHistoryMutation = useUpdateDecisionTreeRunHistoryMutation({
    onSuccess(data) {
      updateRun(data.decisionTree.run.updateHistory);
    },
  });

  const startRunMutation = useStartDecisionTreeRunMutation({
    onSuccess(data, variables) {
      if (variables.input.isPreviewRun) {
        setPreviewRunId(data.decisionTree.run.start.id);
      } else if (!isDecisionTreeRoute || routeRunId !== data.decisionTree.run.start.id) {
        navigate(`/decision-trees/run/${data.decisionTree.run.start.id}`);
      }
      updateRun(data.decisionTree.run.start);
    },
  });

  const stopRunMutation = useStopDecisionTreeRunMutation({
    onSuccess(data) {
      updateRun(data.decisionTree.run.stop);
      if (isPreview) {
        setPreviewRunId(null);
      } else {
        navigate('/decision-trees', true);
      }
    },
  });

  const writeCommentMutation = useWriteDecisionTreeRunCommentMutation({
    onSuccess(data) {
      updateRun(data.decisionTree.run.writeComment);
    },
  });

  const chooseChild = React.useCallback((nodeId: string, value: string, childId: string) => {
    if (!decisionTreeRun.data) {
      throw new Error('Decision tree run not found');
    }
    const currentHistory = decisionTreeRun.data.getDecisionTreeRun.history;
    if (currentHistory.length === 0) {
      throw new Error('No history found');
    }

    const historyUpToNow = [];
    currentHistory.some((h) => {
      if (h.nodeId === nodeId) {
        return true;
      }
      historyUpToNow.push({ nodeId: h.nodeId, value: h.value });
      return false;
    });

    const updatedHistory = [...historyUpToNow, { nodeId, value }, { nodeId: childId }];

    updateDecisionTreeRunHistoryMutation.mutate({
      input: {
        id: runId ?? '',
        history: updatedHistory,
      },
    });
  }, [decisionTreeRun.data, runId, updateDecisionTreeRunHistoryMutation]);

  const toggleSequenceValue = React.useCallback((value: string) => {
    if (!decisionTreeRun.data) {
      throw new Error('Decision tree run not found');
    }
    const currentHistory = decisionTreeRun.data.getDecisionTreeRun.history;
    if (currentHistory.length === 0) {
      throw new Error('No history found');
    }
    const lastNode = currentHistory.slice(-1)[0];
    const currentValues = lastNode.sequenceValues ?? [];
    const updatedValues = currentValues.includes(value) ? currentValues.filter((v) => v !== value) : [...currentValues, value];
    const updatedHistory = [
      ...currentHistory.slice(0, -1).map((node) => ({ nodeId: node.nodeId, value: node.value })),
      {
        nodeId: lastNode.nodeId,
        sequenceValues: updatedValues,
      },
    ];

    updateDecisionTreeRunHistoryMutation.mutate({
      input: {
        id: runId ?? '',
        history: updatedHistory,
      },
    });
  }, [decisionTreeRun.data, runId, updateDecisionTreeRunHistoryMutation]);

  const start = React.useCallback((id: string, parentId: string, isPreviewRun: boolean) => {
    startRunMutation.mutate({
      input: {
        id,
        projectId,
        parentId,
        isPreviewRun,
        sessionId,
      },
    });
    if (parentId) {
      queryClient.invalidateQueries(['getDecisionTreeRun', { input: { id: parentId } }]);
    }
  }, [projectId, queryClient, sessionId, startRunMutation]);

  const resume = React.useCallback((id: string) => {
    if (isPreview) {
      setPreviewRunId(id);
    } else {
      navigate(`/decision-trees/run/${id}`);
    }
  }, [isPreview, navigate]);

  const writeComment = React.useCallback((nodeId: string, userComment: string) => {
    writeCommentMutation.mutate({
      input: {
        nodeId,
        runId,
        userComment,
      },
    });
  }, [runId, writeCommentMutation]);

  const stop = React.useCallback(() => {
    if (runId) {
      stopRunMutation.mutate({ input: { id: runId } });
    }
  }, [runId, stopRunMutation]);

  const pause = React.useCallback(() => {
    if (isPreview) {
      setPreviewRunId(null);
    } else {
      navigate('/decision-trees', true);
    }
  }, [isPreview, navigate]);

  const value = React.useMemo(() => ({
    runId: runId ?? null,
    run: decisionTreeRun.data?.getDecisionTreeRun ?? null,
    resume,
    writeComment,
    pause,
    start,
    stop,
    chooseChild,
    toggleSequenceValue,
  }), [chooseChild, decisionTreeRun.data?.getDecisionTreeRun, pause, resume, runId, start, stop, toggleSequenceValue, writeComment]);

  return (
    <DecisionTreeRunContext.Provider value={value}>
      {children}
    </DecisionTreeRunContext.Provider>
  );
}
